import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import ApiService from '../services/api';
import useTranslations from '../hooks/useTranslations';
import LanguageSelector from '../components/LanguageSelector';
import './LoginPage.css';
import { Html5Qrcode } from 'html5-qrcode';
import { 
  IceCream, 
  Mail, 
  Lock, 
  Eye, 
  EyeOff, 
  BarChart3,
  Users,
  Package,
  TrendingUp,
  CheckCircle,
  Star,
  Menu,
  X,
  Play,
  ArrowRight,
  Shield,
  MapPin,
  Calendar,
  Cloud,
  Camera,
  Wrench,
  Clock,
  FileText,
  Landmark,
  Receipt
} from 'lucide-react';
import ModuloContatto from '../components/ModuloContatto';
import { MODULO_COMMERCIALE, MODULO_ASSISTENZA, linkModulo } from '../config/moduli';

// Landing Page Styles
const LandingContainer = styled.div`
  min-height: 100vh;
  background: var(--bg-primary);
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  
  &.landing-container {
    scroll-behavior: smooth;
  }
`;

const Header = styled.header`
  position: fixed;
  top: 0;
  width: 100%;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  z-index: 1000;
  border-bottom: 1px solid var(--gray-200);
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const NavLogo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--primary-700);
  font-size: 1.5rem;
  font-weight: bold;
`;

const NavMenu = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled.a`
  color: #666;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;

  &:hover {
    color: var(--primary-700);
  }
`;

const LoginButton = styled.button`
  background: var(--primary-700);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: var(--primary-800);
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const Hero = styled.section`
  padding: 8rem 2rem 4rem;
  text-align: center;
  background: var(--primary-700);
  color: white;
  position: relative;
  overflow: hidden;
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  
  &.hero-content {
    animation: heroFadeIn 0.8s ease-out;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 1rem;
  font-weight: bold;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  margin-bottom: 2rem;
  opacity: 0.9;
  line-height: 1.6;
`;

const HeroButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const PrimaryButton = styled.button`
  background: white;
  color: var(--primary-700);
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: box-shadow 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    box-shadow: var(--shadow-md);
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: white;
  border: 2px solid white;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: white;
    color: var(--primary-700);
  }
`;

const Features = styled.section`
  padding: 4rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const FeatureCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  border: 1px solid var(--gray-200);
  box-shadow: var(--shadow-sm);
  text-align: center;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: var(--shadow-lg);
  }
`;

const FeatureIcon = styled.div`
  color: var(--primary-600);
  margin-bottom: 1rem;
  display: flex;
  justify-content: center;
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #333;
`;

const FeatureDescription = styled.p`
  color: #666;
  line-height: 1.6;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  color: #333;
  margin-bottom: 1rem;
`;

const SectionSubtitle = styled.p`
  text-align: center;
  color: #666;
  font-size: 1.1rem;
  max-width: 600px;
  margin: 0 auto;
`;

const Testimonials = styled.section`
  padding: 4rem 2rem;
  background: #f8f9fa;
`;

const TestimonialsGrid = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 3rem;
`;

const TestimonialCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  border: 1px solid var(--gray-200);
  box-shadow: var(--shadow-sm);
  margin: 1rem;
  max-width: 640px;
`;

const TestimonialContent = styled.p`
  color: #666;
  line-height: 1.6;
  margin-bottom: 1rem;
  font-style: italic;
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const AuthorInfo = styled.div`
  h4 {
    color: #333;
    margin: 0;
  }
  
  p {
    color: #666;
    margin: 0;
    font-size: 0.9rem;
  }
`;

const Contatti = styled.section`
  padding: 4rem 2rem;
  max-width: 720px;
  margin: 0 auto;
`;

const Pricing = styled.section`
  padding: 4rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const PricingCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  border: 1px solid var(--gray-200);
  box-shadow: var(--shadow-sm);
  text-align: center;
  position: relative;
  transition: box-shadow 0.2s ease;

  &.featured {
    border: 2px solid var(--primary-500);
  }
`;

const PricingBadge = styled.div`
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--accent-500);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
`;

const PricingTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #333;
`;

const PricingPrice = styled.div`
  font-size: 3rem;
  font-weight: bold;
  color: var(--primary-700);
  margin-bottom: 0.5rem;
`;

const PricingPeriod = styled.div`
  color: #666;
  margin-bottom: 2rem;
`;

const PricingFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 2rem 0;
`;

const PricingFeature = styled.li`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  color: #666;
`;

const Footer = styled.footer`
  background: #333;
  color: white;
  padding: 3rem 2rem 1rem;
  text-align: center;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const FooterLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const FooterLink = styled.a`
  color: #ccc;
  text-decoration: none;
  transition: color 0.2s;

  &:hover {
    color: var(--primary-200);
  }
`;

// Modal Styles
const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 2rem;
  animation: fadeIn 0.3s ease-out;
  
  /* Mobile fixes */
  @media (max-width: 768px) {
    padding: 1rem;
    align-items: flex-start;
    padding-top: 2rem;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const ModalContent = styled.div`
  background: white;
  padding: 3rem;
  border-radius: 12px;
  box-shadow: var(--shadow-lg);
  width: 100%;
  max-width: 400px;
  position: relative;
  animation: slideIn 0.3s ease-out;

  /* Mobile fixes */
  @media (max-width: 768px) {
    padding: 2rem;
    border-radius: 12px;
    margin-bottom: 2rem;
    min-height: auto;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  
  /* Mobile touch fixes */
  -webkit-appearance: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  padding: 0.5rem;
  
  &:hover {
    color: #333;
  }
  
  &:active {
    color: #000;
  }
  
  /* Mobile specific fixes */
  @media (max-width: 768px) {
    top: 0.5rem;
    right: 0.5rem;
    padding: 0.75rem;
    min-width: 44px;
    min-height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const ModalTitle = styled.h2`
  text-align: center;
  color: #333;
  margin-bottom: 2rem;
`;

const ToggleContainer = styled.div`
  display: flex;
  background: #f8f9fa;
  border-radius: 10px;
  padding: 4px;
  margin-bottom: 2rem;
`;

const ToggleButton = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$active ? 'white' : 'transparent'};
  color: ${props => props.$active ? 'var(--primary-700)' : '#6c757d'};
  box-shadow: ${props => props.$active ? 'var(--shadow-sm)' : 'none'};

  &:hover {
    color: var(--primary-700);
  }
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 1rem;
  border-radius: 10px;
  border: 1px solid #c3e6cb;
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InputGroup = styled.div`
  position: relative;
`;

const InputIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  z-index: 1;
`;

const Input = styled.input`
  width: 100%;
  padding: 1rem 1rem 1rem 3rem;
  border: 2px solid #e9ecef;
  border-radius: 10px;
  font-size: 1rem;
  transition: border-color 0.2s;
  box-sizing: border-box;
  
  /* Mobile touch fixes */
  -webkit-appearance: none;
  -webkit-tap-highlight-color: transparent;
  
  &:focus {
    outline: none;
    border-color: var(--primary-500);
  }

  &::placeholder {
    color: #adb5bd;
  }
  
  /* Mobile specific fixes */
  @media (max-width: 768px) {
    font-size: 16px; /* Prevents zoom on iOS */
    min-height: 48px; /* iOS minimum touch target */
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  
  /* Mobile touch fixes */
  -webkit-appearance: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  
  &:hover {
    background: #f8f9fa;
  }
  
  &:active {
    background: #e9ecef;
  }
  
  /* Mobile specific fixes */
  @media (max-width: 768px) {
    padding: 0.75rem;
    min-width: 44px;
    min-height: 44px;
  }
`;

const SubmitButton = styled.button`
  background: var(--primary-700);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 10px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  width: 100%;

  /* Mobile touch fixes */
  -webkit-appearance: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  &:hover {
    background: var(--primary-800);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  /* Mobile specific fixes */
  @media (max-width: 768px) {
    padding: 1.2rem 2rem;
    font-size: 1rem;
    min-height: 48px; /* iOS minimum touch target */
  }
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 1rem;
  border-radius: 10px;
  border: 1px solid #f5c6cb;
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

const QrLoginButton = styled.button`
  background: var(--accent-600);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 10px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.5rem;

  &:hover {
    background: var(--accent-700);
  }
  
  @media (max-width: 768px) {
    padding: 1.2rem 2rem;
    font-size: 1rem;
    min-height: 48px;
  }
`;

const QrScannerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  width: 100%;
  margin-top: 1rem;
`;

const QrReaderView = styled.div`
  width: 100%;
  max-width: 320px;
  aspect-ratio: 1;
  background: var(--gray-50);
  border: 3px dashed var(--gray-300);
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: inset 0 0 10px rgba(0,0,0,0.05);

  #qr-reader {
    width: 100% !important;
    height: 100% !important;
    
    video {
      object-fit: cover !important;
      width: 100% !important;
      height: 100% !important;
    }
  }
`;

// Main Component
const LoginPage: React.FC = () => {
  const { t } = useTranslations();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [openDirectlyInRegistration, setOpenDirectlyInRegistration] = useState(false);
  
  // Login form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Registration form state
  const [tenantName, setTenantName] = useState('');
  const [puntoVenditaNome, setPuntoVenditaNome] = useState('');
  const [puntoVenditaIndirizzo, setPuntoVenditaIndirizzo] = useState('');
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, loginWithQr } = useAuth();
  const [isScanningQr, setIsScanningQr] = useState(false);

  // Gestione scanner QR
  useEffect(() => {
    let html5QrCode: Html5Qrcode | null = null;
    
    if (isScanningQr && showLoginModal) {
      setError('');
      setSuccess('');
      
      const startScanner = async () => {
        try {
          // Attendi un attimo per assicurarsi che il DOM sia renderizzato
          await new Promise(resolve => setTimeout(resolve, 300));
          
          html5QrCode = new Html5Qrcode("qr-reader");
          await html5QrCode.start(
            { facingMode: "environment" },
            {
              fps: 10,
              qrbox: (width, height) => {
                const size = Math.min(width, height) * 0.7;
                return { width: size, height: size };
              }
            },
            async (decodedText) => {
              console.log("QR Code letto:", decodedText);
              // Ferma lo scanner al primo successo
              if (html5QrCode && html5QrCode.isScanning) {
                await html5QrCode.stop();
              }
              setIsScanningQr(false);
              
              // Esegui login tramite QR
              try {
                setIsLoading(true);
                await loginWithQr(decodedText);
                setShowLoginModal(false);
              } catch (loginErr: any) {
                console.error("Errore login QR:", loginErr);
                setError(loginErr.response?.data?.error || "Codice QR non valido o scaduto");
              } finally {
                setIsLoading(false);
              }
            },
            (errorMessage) => {
              // Ignora errori di scansione minori
            }
          );
        } catch (err: any) {
          console.error("Impossibile avviare la fotocamera:", err);
          setError("Impossibile accedere alla fotocamera. Controlla i permessi del browser.");
          setIsScanningQr(false);
        }
      };

      startScanner();
    }

    return () => {
      if (html5QrCode && html5QrCode.isScanning) {
        html5QrCode.stop().catch(err => console.error("Errore stop scanner in cleanup:", err));
      }
    };
  }, [isScanningQr, showLoginModal, loginWithQr]);

  // Inizializza quando si apre il modal - SEMPLIFICATO
  useEffect(() => {
    if (showLoginModal) {
      // Reset form quando si apre il modal
      setError('');
      setSuccess('');
      setIsScanningQr(false);
      
      // Se deve aprire direttamente in registrazione, imposta la modalità
      if (openDirectlyInRegistration) {
        setIsRegistering(true);
        setOpenDirectlyInRegistration(false);
      } else {
        setIsRegistering(false);
      }
      
      // Reset login form
      setEmail('');
      setPassword('');
      setShowPassword(false);
      
      // Reset registration form
      setTenantName('');
      setPuntoVenditaNome('');
      setPuntoVenditaIndirizzo('');
      setAdminName('');
      setAdminEmail('');
      setAdminPassword('');
      setShowAdminPassword(false);
    }
  }, [showLoginModal, openDirectlyInRegistration]);

  // DISABILITATO: inizializzazione componente
  useEffect(() => {
    // LoginPage component mounted
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setError('');
    setSuccess('');
    setIsLoading(true);

    if (isRegistering) {
      // Handle registration
      if (!tenantName || !puntoVenditaNome || !adminName || !adminEmail || !adminPassword) {
        setError('Tutti i campi obbligatori sono richiesti per la registrazione');
        setIsLoading(false);
        return;
      }

      if (adminPassword.length < 6) {
        setError('La password deve essere di almeno 6 caratteri');
        setIsLoading(false);
        return;
      }

      try {
        // Usa ApiService per gestire correttamente l'ambiente
        await ApiService.registerTenant({
          tenant_name: tenantName,
          punto_vendita_nome: puntoVenditaNome,
          punto_vendita_indirizzo: puntoVenditaIndirizzo,
          admin_name: adminName,
          admin_email: adminEmail,
          admin_password: adminPassword,
        });

        setSuccess('Registrazione completata. Ora puoi effettuare il login.');
        setIsRegistering(false);
        
        // Pre-compila il form di login con l'email appena registrata
        setEmail(adminEmail);
        setPassword('');
        
        // Reset registration form
        setTenantName('');
        setPuntoVenditaNome('');
        setPuntoVenditaIndirizzo('');
        setAdminName('');
        setAdminEmail('');
        setAdminPassword('');
        
      } catch (error: any) {
        const errorMessage = error.message || 'Errore durante la registrazione';
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    } else {
      // Handle login
      if (!email || !password) {
        const errorMsg = 'Email e password sono richiesti';
        setError(errorMsg);
        setIsLoading(false);
        return;
      }

      try {
        await login(email, password);
        setShowLoginModal(false);
      } catch (error: any) {
        const errorMessage = error.response?.data?.error || 'Errore durante il login. Verifica le credenziali.';
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <LandingContainer className="landing-container">
      <Header>
        <Nav>
          <NavLogo>
            <IceCream size={32} />
            {t('presentation.title', 'Sistema Gelato')}
          </NavLogo>
          
          <NavMenu>
            <NavLink onClick={() => scrollToSection('features')}>{t('presentation.navigation.features', 'Funzionalità')}</NavLink>
            <NavLink onClick={() => scrollToSection('testimonials')}>{t('presentation.navigation.testimonials', 'Testimonianze')}</NavLink>
            <NavLink onClick={() => scrollToSection('pricing')}>{t('presentation.navigation.pricing', 'Prezzi')}</NavLink>
          </NavMenu>

          <NavActions>
            <LanguageSelector />
            <LoginButton onClick={() => {
              setShowLoginModal(true);
            }}>
              {t('presentation.cta.login', 'Accedi')}
            </LoginButton>
          </NavActions>
          
          <MobileMenuButton onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </MobileMenuButton>
        </Nav>
      </Header>

      <Hero>
        <HeroContent className="hero-content">
          <HeroTitle>{t('presentation.title', 'Sistema Gelato')}</HeroTitle>
          <HeroSubtitle>
            {t('presentation.subtitle', 'La soluzione completa per produzione, analytics e gestione della tua gelateria')}
          </HeroSubtitle>
          <HeroButtons>
            <PrimaryButton onClick={() => {
              setOpenDirectlyInRegistration(true);
              setShowLoginModal(true);
            }}>
              <Play size={20} />
              {t('presentation.cta.register', 'Registra la tua Gelateria')}
            </PrimaryButton>
            <SecondaryButton onClick={() => scrollToSection('features')}>
              <ArrowRight size={20} />
              {t('presentation.cta.discover', 'Scopri cosa fa')}
            </SecondaryButton>
          </HeroButtons>
        </HeroContent>
      </Hero>


      <Features id="features">
        <SectionTitle>{t('presentation.benefits.title', 'Perché Scegliere Sistema Gelato?')}</SectionTitle>
        <SectionSubtitle>
          {t('presentation.subtitle', 'La soluzione completa per produzione, analytics e gestione della tua gelateria')}
        </SectionSubtitle>
        
        <FeaturesGrid>
          <FeatureCard className="feature-card">
            <FeatureIcon>
              <BarChart3 size={48} />
            </FeatureIcon>
            <FeatureTitle>{t('presentation.features.analytics.title', 'Analytics Avanzate')}</FeatureTitle>
            <FeatureDescription>
              {t('presentation.features.analytics.description', 'Visualizza statistiche dettagliate su produzione, costi e tendenze')}
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard className="feature-card">
            <FeatureIcon>
              <IceCream size={48} />
            </FeatureIcon>
            <FeatureTitle>{t('presentation.features.production.title', 'Gestione Produzione')}</FeatureTitle>
            <FeatureDescription>
              {t('presentation.features.production.description', 'Traccia la produzione giornaliera di ogni gusto con precisione')}
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard className="feature-card">
            <FeatureIcon>
              <Package size={48} />
            </FeatureIcon>
            <FeatureTitle>{t('presentation.features.costs.title', 'Controllo Costi')}</FeatureTitle>
            <FeatureDescription>
              {t('presentation.features.costs.description', 'Monitora il food cost e ottimizza la redditività')}
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard className="feature-card">
            <FeatureIcon>
              <Users size={48} />
            </FeatureIcon>
            <FeatureTitle>{t('presentation.features.multistore.title', 'Multi-Punto Vendita')}</FeatureTitle>
            <FeatureDescription>
              {t('presentation.features.multistore.description', 'Gestisci più punti vendita da un\'unica dashboard')}
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard className="feature-card">
            <FeatureIcon>
              <TrendingUp size={48} />
            </FeatureIcon>
            <FeatureTitle>{t('presentation.features.optimization.title', 'Ottimizzazione Costi')}</FeatureTitle>
            <FeatureDescription>
              {t('presentation.features.optimization.description', 'Calcola automaticamente i costi di produzione, analizza la marginalità e ricevi suggerimenti per massimizzare i profitti.')}
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard className="feature-card">
            <FeatureIcon>
              <Calendar size={48} />
            </FeatureIcon>
            <FeatureTitle>{t('presentation.features.calendar.title', 'Calendario Produzione')}</FeatureTitle>
            <FeatureDescription>
              {t('presentation.features.calendar.description', 'Visualizza e pianifica la produzione su calendario interattivo con statistiche mensili, filtri per punto vendita e correlazioni meteo-produzione.')}
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard className="feature-card">
            <FeatureIcon>
              <Cloud size={48} />
            </FeatureIcon>
            <FeatureTitle>{t('presentation.features.weather.title', 'Sistema Meteo Integrato')}</FeatureTitle>
            <FeatureDescription>
              {t('presentation.features.weather.description', 'Dati meteorologici real-time per ogni punto vendita, previsioni a 7 giorni e analytics predittive per ottimizzare la produzione in base al meteo.')}
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard className="feature-card">
            <FeatureIcon>
              <Shield size={48} />
            </FeatureIcon>
            <FeatureTitle>{t('presentation.features.security.title', 'Sicurezza Enterprise')}</FeatureTitle>
            <FeatureDescription>
              {t('presentation.features.security.description', 'Backup automatici, crittografia avanzata, accesso sicuro e conformità alle normative per proteggere i tuoi dati.')}
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard className="feature-card">
            <FeatureIcon>
              <Landmark size={48} />
            </FeatureIcon>
            <FeatureTitle>{t('presentation.features.gestione.title', 'Controllo di Gestione')}</FeatureTitle>
            <FeatureDescription>
              {t('presentation.features.gestione.description', 'Conto economico completo: i ricavi dal venduto in cassa, i costi dall\'estratto conto della banca, importato e classificato da solo. Margine di contribuzione ed EBITDA, con tasse e finanziamenti al posto giusto.')}
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard className="feature-card">
            <FeatureIcon>
              <Receipt size={48} />
            </FeatureIcon>
            <FeatureTitle>{t('presentation.features.riconciliazione.title', 'Riconciliazione degli Incassi')}</FeatureTitle>
            <FeatureDescription>
              {t('presentation.features.riconciliazione.description', 'Confronta quanto e\' passato dalla cassa con quanto e\' arrivato davvero in banca, canale per canale. La differenza sono le commissioni: finalmente si vede quanto costano i partner bancari.')}
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard className="feature-card">
            <FeatureIcon>
              <FileText size={48} />
            </FeatureIcon>
            <FeatureTitle>{t('presentation.features.personale.title', 'Personale e Buste Paga')}</FeatureTitle>
            <FeatureDescription>
              {t('presentation.features.personale.description', 'L\'archivio dei documenti del personale: si carica tutto in una volta e ogni dipendente trova i propri, e soltanto i propri. Dai cedolini si leggono netto, TFR e paga oraria, e la scheda dice quanto costa davvero una persona.')}
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard className="feature-card">
            <FeatureIcon>
              <Clock size={48} />
            </FeatureIcon>
            <FeatureTitle>{t('presentation.features.turni.title', 'Turni e Timbrature')}</FeatureTitle>
            <FeatureDescription>
              {t('presentation.features.turni.description', 'Pianificazione dei turni, scambi fra colleghi e importazione delle timbrature dal sistema di rilevazione presenze, per confrontare le ore programmate con quelle davvero lavorate.')}
            </FeatureDescription>
          </FeatureCard>
        </FeaturesGrid>
      </Features>

      <Testimonials id="testimonials">
        <SectionTitle>{t('presentation.testimonials.title', 'Chi lo usa')}</SectionTitle>
        <SectionSubtitle>
          {t('presentation.testimonials.subtitle', 'Sistema Gelato nasce dal lavoro quotidiano di una gelateria vera')}
        </SectionSubtitle>

        {/* Una sola testimonianza, perche' una sola gelateria lo usa davvero.
            Le quattro precedenti erano inventate: su un prodotto che si vuole
            vendere, una recensione falsa e' un problema serio, non un
            riempitivo grafico. */}
        <TestimonialsGrid>
          <TestimonialCard>
            <TestimonialContent>
              {t('presentation.testimonials.testimonial1.text', 'Il sistema è cresciuto insieme alla nostra gelateria: prima la produzione e il food cost, poi i turni, e ora il controllo di gestione, che finalmente ci dice quanto guadagniamo davvero e quanto ci costano le commissioni bancarie. Le buste paga e i documenti del personale sono tutti lì, e ogni ragazza trova i suoi.')}
            </TestimonialContent>
            <TestimonialAuthor>
              <div style={{ display: 'flex', color: 'var(--accent-500)' }}>
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
              </div>
              <AuthorInfo>
                <h4>{t('presentation.testimonials.testimonial1.author', 'Vittoria Bortolazzo')}</h4>
                <p>{t('presentation.testimonials.testimonial1.business', 'Gelato Giusto, Milano')}</p>
              </AuthorInfo>
            </TestimonialAuthor>
          </TestimonialCard>
        </TestimonialsGrid>
      </Testimonials>

      <Pricing id="pricing">
        <SectionTitle>Piani su Misura</SectionTitle>
        <SectionSubtitle>
          Scegli il piano perfetto per le tue esigenze. Inizia gratis e scala quando vuoi.
        </SectionSubtitle>
        
        <PricingGrid>
          <PricingCard className="pricing-card">
            <PricingTitle>Starter</PricingTitle>
            <PricingPrice>€29</PricingPrice>
            <PricingPeriod>al mese</PricingPeriod>
            <PricingFeatures>
              <PricingFeature>
                <CheckCircle size={16} color="#7d8202" />
                1 Punto Vendita
              </PricingFeature>
              <PricingFeature>
                <CheckCircle size={16} color="#7d8202" />
                Fino a 3 Utenti
              </PricingFeature>
              <PricingFeature>
                <CheckCircle size={16} color="#7d8202" />
                Gestione Produzione
              </PricingFeature>
              <PricingFeature>
                <CheckCircle size={16} color="#7d8202" />
                Analytics Base
              </PricingFeature>
              <PricingFeature>
                <CheckCircle size={16} color="#7d8202" />
                Supporto Email
              </PricingFeature>
            </PricingFeatures>
            <PrimaryButton onClick={() => {
              setShowLoginModal(true);
            }}>
              Inizia Gratis
            </PrimaryButton>
          </PricingCard>

          <PricingCard className="pricing-card featured">
            <PricingBadge>PIÙ POPOLARE</PricingBadge>
            <PricingTitle>Professional</PricingTitle>
            <PricingPrice>€79</PricingPrice>
            <PricingPeriod>al mese</PricingPeriod>
            <PricingFeatures>
              <PricingFeature>
                <CheckCircle size={16} color="#7d8202" />
                3 Punti Vendita
              </PricingFeature>
              <PricingFeature>
                <CheckCircle size={16} color="#7d8202" />
                Utenti Illimitati
              </PricingFeature>
              <PricingFeature>
                <CheckCircle size={16} color="#7d8202" />
                Analytics Avanzate
              </PricingFeature>
              <PricingFeature>
                <CheckCircle size={16} color="#7d8202" />
                Calcolo PAC e Valori Nutrizionali
              </PricingFeature>
              <PricingFeature>
                <CheckCircle size={16} color="#7d8202" />
                Ottimizzazione Costi
              </PricingFeature>
              <PricingFeature>
                <CheckCircle size={16} color="#7d8202" />
                Supporto Prioritario
              </PricingFeature>
            </PricingFeatures>
            <PrimaryButton onClick={() => {
              setShowLoginModal(true);
            }}>
              Prova 30 Giorni
            </PrimaryButton>
          </PricingCard>

          <PricingCard className="pricing-card">
            <PricingTitle>Enterprise</PricingTitle>
            <PricingPrice>€199</PricingPrice>
            <PricingPeriod>al mese</PricingPeriod>
            <PricingFeatures>
              <PricingFeature>
                <CheckCircle size={16} color="#7d8202" />
                Punti Vendita Illimitati
              </PricingFeature>
              <PricingFeature>
                <CheckCircle size={16} color="#7d8202" />
                Personalizzazioni
              </PricingFeature>
              <PricingFeature>
                <CheckCircle size={16} color="#7d8202" />
                API Integrate
              </PricingFeature>
              <PricingFeature>
                <CheckCircle size={16} color="#7d8202" />
                Analisi Scientifica Avanzata
              </PricingFeature>
              <PricingFeature>
                <CheckCircle size={16} color="#7d8202" />
                Backup Avanzati
              </PricingFeature>
              <PricingFeature>
                <CheckCircle size={16} color="#7d8202" />
                Consulenza Dedicata
              </PricingFeature>
            </PricingFeatures>
            <PrimaryButton onClick={() => scrollToSection('contact')}>
              Contattaci
            </PrimaryButton>
          </PricingCard>
        </PricingGrid>
      </Pricing>

      {/* Il modulo commerciale. Prima di questa sezione la vetrina non
          raccoglieva niente: «Contattaci» apriva il form di accesso e i link
          #contact e #support del footer erano due ancore morte. Le richieste
          vanno in controll.omai.cloud/richieste, coda commerciale.
          L'assistenza sta invece nell'area riservata: i due moduli non
          devono trovarsi sulla stessa schermata. */}
      <Contatti id="contact">
        <SectionTitle>Parliamone</SectionTitle>
        <ModuloContatto chiave={MODULO_COMMERCIALE} />
      </Contatti>

      <Footer>
        <FooterContent>
          <FooterLinks>
            <FooterLink 
              href="#privacy"
              onClick={(e) => {
                e.preventDefault();
                (window as any).onNavigate?.('privacy-policy');
              }}
            >
              {t('footer.privacy', 'Privacy Policy')}
            </FooterLink>
            <FooterLink 
              href="#terms"
              onClick={(e) => {
                e.preventDefault();
                (window as any).onNavigate?.('terms-of-service');
              }}
            >
              {t('footer.terms', 'Termini di Servizio')}
            </FooterLink>
            <FooterLink
              href={linkModulo(MODULO_ASSISTENZA)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('footer.support', 'Supporto')}
            </FooterLink>
            <FooterLink href="#contact">{t('footer.contact', 'Contatti')}</FooterLink>
            <FooterLink 
              href="/superadmin" 
              style={{ 
                fontSize: '0.75rem', 
                opacity: 0.5,
                color: '#888'
              }}
            >
              <Wrench size={14} />
            </FooterLink>
          </FooterLinks>
          <p>&copy; 2024 Sistema Gelato. Tutti i diritti riservati.</p>
        </FooterContent>
      </Footer>

      {/* Login Modal */}
      {showLoginModal && (
        <Modal>
          <ModalContent>
            <CloseButton onClick={() => {
              setShowLoginModal(false);
            }}>
              <X size={24} />
            </CloseButton>
            
            <ModalTitle>
              {isRegistering ? 'Registra la tua Gelateria' : 'Accedi a Sistema Gelato'}
            </ModalTitle>

            <ToggleContainer>
              <ToggleButton 
                type="button"
                $active={!isRegistering} 
                onClick={() => {
                  setIsRegistering(false);
                  setIsScanningQr(false);
                  setError('');
                  setSuccess('');
                }}
              >
                Accedi
              </ToggleButton>
              <ToggleButton 
                type="button"
                $active={isRegistering} 
                onClick={() => {
                  setIsRegistering(true);
                  setIsScanningQr(false);
                  setError('');
                  setSuccess('');
                }}
              >
                Registrati
              </ToggleButton>
            </ToggleContainer>

            {error && <ErrorMessage>{error}</ErrorMessage>}
            {success && <SuccessMessage>{success}</SuccessMessage>}

            {isScanningQr && !isRegistering ? (
              <QrScannerContainer>
                <div style={{ textAlign: 'center', color: '#666', fontSize: '0.95rem' }}>
                  Inquadra il tuo codice QR per accedere rapidamente.
                </div>
                <QrReaderView>
                  <div id="qr-reader" />
                </QrReaderView>
                <SubmitButton 
                  type="button" 
                  onClick={() => setIsScanningQr(false)}
                  style={{ background: 'var(--gray-500)', marginTop: '1rem' }}
                >
                  Torna al login classico
                </SubmitButton>
              </QrScannerContainer>
            ) : (
              <Form onSubmit={handleSubmit}>
                {isRegistering ? (
                  // Registration Form
                  <>
                    <InputGroup>
                      <InputIcon>
                        <IceCream size={20} />
                      </InputIcon>
                      <Input
                        type="text"
                        placeholder="Nome della Gelateria"
                        value={tenantName}
                        onChange={(e) => setTenantName(e.target.value)}
                        required
                        autoComplete="organization"
                        autoCapitalize="words"
                        autoCorrect="off"
                        spellCheck="false"
                      />
                    </InputGroup>

                    <InputGroup>
                      <InputIcon>
                        <MapPin size={20} />
                      </InputIcon>
                      <Input
                        type="text"
                        placeholder="Nome Punto Vendita *"
                        value={puntoVenditaNome}
                        onChange={(e) => setPuntoVenditaNome(e.target.value)}
                        required
                        autoComplete="off"
                        autoCapitalize="words"
                        autoCorrect="off"
                        spellCheck="false"
                      />
                    </InputGroup>

                    <InputGroup>
                      <InputIcon>
                        <MapPin size={20} />
                      </InputIcon>
                      <Input
                        type="text"
                        placeholder="Indirizzo Punto Vendita (opzionale)"
                        value={puntoVenditaIndirizzo}
                        onChange={(e) => setPuntoVenditaIndirizzo(e.target.value)}
                        autoComplete="street-address"
                        autoCapitalize="words"
                        autoCorrect="off"
                        spellCheck="false"
                      />
                    </InputGroup>

                    <InputGroup>
                      <InputIcon>
                        <Users size={20} />
                      </InputIcon>
                      <Input
                        type="text"
                        placeholder="Nome Amministratore"
                        value={adminName}
                        onChange={(e) => setAdminName(e.target.value)}
                        required
                        autoComplete="name"
                        autoCapitalize="words"
                        autoCorrect="off"
                        spellCheck="false"
                      />
                    </InputGroup>

                    <InputGroup>
                      <InputIcon>
                        <Mail size={20} />
                      </InputIcon>
                      <Input
                        type="email"
                        placeholder="Email Amministratore"
                        value={adminEmail}
                        onChange={(e) => setAdminEmail(e.target.value)}
                        required
                        autoComplete="email"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck="false"
                      />
                    </InputGroup>

                    <InputGroup>
                      <InputIcon>
                        <Lock size={20} />
                      </InputIcon>
                      <Input
                        type={showAdminPassword ? 'text' : 'password'}
                        placeholder="Password (min. 6 caratteri)"
                        value={adminPassword}
                        onChange={(e) => setAdminPassword(e.target.value)}
                        required
                        minLength={6}
                        autoComplete="new-password"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck="false"
                      />
                      <PasswordToggle
                        type="button"
                        onClick={() => setShowAdminPassword(!showAdminPassword)}
                      >
                        {showAdminPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </PasswordToggle>
                    </InputGroup>

                    <SubmitButton type="submit" disabled={isLoading}>
                      {isLoading ? 'Registrazione in corso...' : 'Registra Gelateria'}
                    </SubmitButton>
                  </>
                ) : (
                  // Login Form
                  <>
                    <InputGroup>
                      <InputIcon>
                        <Mail size={20} />
                      </InputIcon>
                      <Input
                        type="email"
                        placeholder="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        autoComplete="email"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck="false"
                      />
                    </InputGroup>

                    <InputGroup>
                      <InputIcon>
                        <Lock size={20} />
                      </InputIcon>
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        autoComplete="current-password"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck="false"
                      />
                      <PasswordToggle
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </PasswordToggle>
                    </InputGroup>

                    <SubmitButton type="submit" disabled={isLoading}>
                      {isLoading ? 'Accesso in corso...' : 'Accedi'}
                    </SubmitButton>

                    <div style={{ display: 'flex', alignItems: 'center', margin: '0.5rem 0', color: '#adb5bd' }}>
                      <div style={{ flex: 1, height: '1px', background: '#e9ecef' }} />
                      <span style={{ padding: '0 0.5rem', fontSize: '0.85rem' }}>oppure</span>
                      <div style={{ flex: 1, height: '1px', background: '#e9ecef' }} />
                    </div>

                    <QrLoginButton type="button" onClick={() => setIsScanningQr(true)} disabled={isLoading}>
                      <Camera size={20} style={{ marginRight: '0.5rem' }} />
                      Accedi con QR Code
                    </QrLoginButton>
                  </>
                )}
              </Form>
            )}

            {!isRegistering && (
              <div style={{ 
                textAlign: 'center', 
                marginTop: '1.5rem', 
                padding: '1rem', 
                background: '#f8f9fa', 
                borderRadius: '10px',
                fontSize: '0.9rem',
                color: '#6c757d'
              }}>
                Non hai ancora un account?{' '}
                <button
                  type="button"
                  onClick={() => setIsRegistering(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--primary-700)',
                    cursor: 'pointer',
                    fontWeight: '600',
                    textDecoration: 'underline'
                  }}
                >
                  Registra la tua gelateria
                </button>
              </div>
            )}

            {isRegistering && (
              <div style={{ 
                textAlign: 'center', 
                marginTop: '1.5rem', 
                padding: '1rem', 
                background: 'var(--primary-50)',
                borderRadius: '10px',
                fontSize: '0.9rem',
                color: 'var(--primary-800)',
                lineHeight: '1.5'
              }}>
                <strong>Prova gratuita per 30 giorni</strong><br />
                Dopo la registrazione potrai configurare la tua sottoscrizione e iniziare subito a gestire la tua gelateria.
              </div>
            )}
          </ModalContent>
        </Modal>
      )}
    </LandingContainer>
  );
};

export default LoginPage;
